import { defineComponent as _defineComponent } from 'vue'
import { ComponentPublicInstance, computed, onBeforeMount, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { BModal } from 'bootstrap-vue';
import { groupBy } from 'lodash';

import * as optionSetsApi from '@/api/shopify/optionSets';

import { OptionSetDto } from '@extTypes/options/optionSet';
import { StoreType } from '@extTypes/api/customily/user';
import { CustomilyTemplateDto } from '@extTypes/api/customily/template';
import { AuditEntity } from '@extTypes/auditLog/types';
import { LinkedOptionSet } from '@extTypes/enums/option-sets';
import { EntityVersionRestored } from '@extTypes/versioning';

import config from '@/config/config';
import { useStore } from '@/store';
import { useEventListener } from '@/composables/eventListener';
import { useTableRowsPerPage } from '@/composables/useTableRowsPerPage';

import * as storeUtil from '@/utils/stores';
import { DateFormat, formatDate } from '@/utils/dates';
import { useModal } from '@/utils/bootstrapVueUtils';
import { linkedOptionSets } from '@/utils/constants/option-sets';

import { FieldType, TableField } from '@/typings/dynamicTable';

import Loader from '@/components/Loader/Loader.vue';
import CustomilyProductsList from '@/components/CustomilyProductsList/CustomilyProductsList.vue';
import AuditLog from '@/components/AuditLog/AuditLog.vue';
import SetProgression from '@/components/Set/SetProgression/SetProgression.vue';
import CustomCheckbox from '@/components/shared/CustomCheckbox.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import DynamicTable from '@/components/shared/DynamicTable.vue';
import CInput from '@/components/shared/CInput.vue';
import EditableText from '@/components/EditableText/EditableText.vue';
import OptionSetLinkedToProductsModal from './OptionSetLinkedToProductsModal/OptionSetLinkedToProductsModal.vue';

interface ProductUsingSet {
    title: string;
    id: string;
    isDesign: boolean;
}

interface SetUsedByProducts {
    setId: string;
    setName: string;
    products: ProductUsingSet[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Sets',
  setup(__props) {

const CURRENT_PAGE_STORAGE_KEY = 'customily-current-sets-page';
const FILTERS_STORAGE_KEY = 'lastSessionOptionSetsFilters';
const FALLBACK_SETS_PER_PAGE = 10;
const LOADING_SETS_MESSAGE = 'Loading sets';
const EXPORTING_SETS_MESSAGE = 'Exporting sets';
const DELETE_CONFIRM_OPTIONS = {
    centered: true,
    noCloseOnBackdrop: true,
    noCloseOnEsc: true,
    okTitle: 'Delete',
    okVariant: 'danger',
    footerClass: 'justify-content-between'
};
const BETWEEN_DOWNLOADS_MS = 500;

const readStoredPage = (): number => {
    const storedPage = localStorage.getItem(CURRENT_PAGE_STORAGE_KEY);
    return (storedPage && Number.parseInt(storedPage, 10)) || 1;
};

const readStoredFilter = (): LinkedOptionSet => {
    try {
        const filters = JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY) || '{}');
        return filters.onlyLinkedOptionSets || LinkedOptionSet.All;
    } catch {
        return LinkedOptionSet.All;
    }
};

const { state, commit } = useStore();
const bvModal = useModal();
const router = useRouter();
const route = useRoute();

// The route guards already ensure there is an authenticated user before this view renders
const user = computed(() => state.user!);
const shop = computed(() => state.store);
const forDesignTemplatesMode = computed(() => state.forDesignTemplatesMode);

const optionSets = ref<OptionSetDto[]>([]);
const selectedSets = ref<OptionSetDto[]>([]);
const selectedSet = ref<OptionSetDto | null>(null);
const selectedOptionSet = ref<OptionSetDto | undefined>();
const productsUsingDeletedSet = ref<ProductUsingSet[]>([]);
const productsUsingDeletedSets = ref<SetUsedByProducts[]>([]);
const optionSetsZipFile = ref<File | null>(null);
const setTaskIdentifier = ref('');
const copiedId = ref('');

const showLoader = ref(false);
const loaderMessage = ref(LOADING_SETS_MESSAGE);
const pageLoaded = ref(false);
const showAuditLogModal = ref(false);
const showOptionSetLinkedToProductsModal = ref(false);

const sortBy = ref('updated_at');
const sortMode = ref<'ASC' | 'DESC'>('DESC');
const onlyLinkedOptionSets = ref<LinkedOptionSet>(readStoredFilter());
const entityType = AuditEntity.OPTION_SET;

// Seeded from `localStorage` before the page watcher below is registered, so restoring the last
// visited page does not count as a page change. `pageSize` is replaced by the measured one before
// the first fetch.
const pagination = reactive({
    searchValue: '',
    currentPage: readStoredPage(),
    pageSize: FALLBACK_SETS_PER_PAGE,
    totalItemCount: 0
});

const setsList = ref<ComponentPublicInstance>();
const setInUseModal = ref<BModal>();
const uploadFileModal = ref<BModal>();
const templatePickerModal = ref<BModal>();
const optionSetsLinkedToProductsModal = ref<BModal>();

const isManaged = computed(() => user.value.customilyUser.IsManaged);
const isCustomStore = computed(() => storeUtil.isCustomStore(shop.value, user.value.customilyUser.Stores));
const isAllChecked = computed(() => selectedSets.value.length > 0);
const optionSetsFiltersLabel = computed(
    () => linkedOptionSets.find((option) => option.value === onlyLinkedOptionSets.value)?.text ?? 'All option sets'
);

const tableConfig: TableField[] = [
    {
        key: 'check',
        label: '',
        thStyle: { width: '48px', padding: '0 0.75rem 0.75rem 0.75rem' }
    },
    {
        key: 'title',
        label: 'Option set',
        type: FieldType.Sort,
        sortKey: 'optionset_name',
        thStyle: { padding: '0 0.75rem 0.75rem 0.75rem' }
    },
    {
        key: 'created_at',
        label: 'Created',
        formatter: (value) => formatDate(value, DateFormat.MMMddYYYYTime),
        thStyle: { width: '180px' },
        type: FieldType.Sort,
        sortKey: 'created_at'
    },
    {
        key: 'updated_at',
        label: 'Last Modified',
        formatter: (value) => formatDate(value, DateFormat.MMMddYYYYTime),
        thStyle: { width: '180px', padding: '0 0.75rem 0.75rem 0.75rem' },
        type: FieldType.Sort,
        sortKey: 'updated_at'
    },
    {
        key: 'linked',
        label: 'Linked to Product',
        thStyle: { width: '145px', padding: '0 0.75rem 0.75rem 0.75rem' }
    },
    { key: 'actions', label: '', thStyle: { width: '250px', padding: '0 0.75rem 0.75rem 0.75rem' } }
];

const loadSets = async () => {
    try {
        showLoader.value = true;
        optionSets.value = await optionSetsApi.getOptionSets(
            pagination.searchValue,
            (pagination.currentPage - 1) * pagination.pageSize,
            pagination.pageSize,
            forDesignTemplatesMode.value,
            onlyLinkedOptionSets.value,
            undefined,
            sortBy.value,
            sortMode.value
        );
    } catch (error) {
        window.captureException(error as Error);
    } finally {
        showLoader.value = false;
    }
};

const getSetsCount = async () => {
    try {
        const { count } = await optionSetsApi.getOptionSetsCount(
            pagination.searchValue,
            forDesignTemplatesMode.value,
            onlyLinkedOptionSets.value
        );
        pagination.totalItemCount = count;
    } catch (error) {
        window.captureException(error as Error);
    }
};

/** Reloads the list and the total count for the current query. */
const refreshSets = async () => {
    await Promise.all([getSetsCount(), loadSets()]);
};

/**
 * Set while the page is changed from our own code, which already reloads the list on its own.
 * Without it, resetting to the first page would fire a second request for the same list.
 */
let skipNextPageReload = false;

const goToFirstPage = () => {
    if (pagination.currentPage === 1) return;
    skipNextPageReload = true;
    pagination.currentPage = 1;
};

watch(
    () => pagination.currentPage,
    (page) => {
        localStorage.setItem(CURRENT_PAGE_STORAGE_KEY, page.toString());

        if (skipNextPageReload) {
            skipNextPageReload = false;
            return;
        }

        loadSets();
    }
);

/** Reloads from the first page. Search, sort and filter changes all go through here. */
const applyQueryChange = async () => {
    goToFirstPage();
    await refreshSets();
};

watch(() => pagination.searchValue, applyQueryChange);

const { whenMeasured } = useTableRowsPerPage({
    anchor: setsList,
    rowHeight: 85,
    reservedHeight: 180,
    fallbackRows: FALLBACK_SETS_PER_PAGE,
    onChange: (rowsPerPage) => {
        pagination.pageSize = rowsPerPage;
        applyQueryChange();
    }
});

const sortSets = (sortKey?: string) => {
    if (!sortKey) return;

    sortBy.value = sortKey;
    sortMode.value = 'DESC';
    applyQueryChange();
};

const sortModeSets = (event: MouseEvent) => {
    event.stopPropagation();
    sortMode.value = sortMode.value === 'DESC' ? 'ASC' : 'DESC';
    applyQueryChange();
};

const filterOptionSets = async (filter = LinkedOptionSet.All) => {
    onlyLinkedOptionSets.value = filter;
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ onlyLinkedOptionSets: filter }));
    await applyQueryChange();
};

const isSetIdSelected = (setId: string) => selectedSets.value.some((s) => s.id === setId);

const toggleSetSelection = (set: OptionSetDto) => {
    // Compared by id because a reload replaces the objects backing the same sets
    selectedSets.value = isSetIdSelected(set.id)
        ? selectedSets.value.filter((s) => s.id !== set.id)
        : [...selectedSets.value, set];
};

const checkAll = () => {
    selectedSets.value = isAllChecked.value ? [] : [...optionSets.value];
};

const handleOptionSetsBulkDelete = async () => {
    const count = selectedSets.value.length;
    const confirmed = await bvModal?.msgBoxConfirm(
        `Are you sure you want to delete ${count} Option ${count === 1 ? 'Set' : 'Sets'}?`,
        { ...DELETE_CONFIRM_OPTIONS, title: 'Delete Option Set(s)' }
    );
    if (!confirmed) return;

    try {
        showLoader.value = true;

        const linkedSets = selectedSets.value.filter((set) => set.linked);
        productsUsingDeletedSets.value = await Promise.all(
            linkedSets.map(async (set) => ({
                setId: set.id,
                setName: set.title,
                products: await optionSetsApi.getProductsWithSet(set.id)
            }))
        );

        const unlinkedOptionSetIds = selectedSets.value.filter((set) => !set.linked).map((set) => set.id);

        // Sets in use cannot be deleted, the user is shown which products still use them
        if (!unlinkedOptionSetIds.length) {
            showLoader.value = false;
            optionSetsLinkedToProductsModal.value?.show();
            return;
        }

        await optionSetsApi.bulkDeleteOptionsSets(unlinkedOptionSetIds);
        await refreshSets();
        selectedSets.value = selectedSets.value.filter((set) => set.linked);
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const removeSet = async (setToDelete: OptionSetDto) => {
    const confirmed = await bvModal?.msgBoxConfirm(
        `Are you sure you want to delete the "${setToDelete.title}" option set?`,
        { ...DELETE_CONFIRM_OPTIONS, title: 'Delete set' }
    );
    if (!confirmed) return;

    try {
        showLoader.value = true;
        productsUsingDeletedSet.value = await optionSetsApi.getProductsWithSet(setToDelete.id);

        // Don't remove the set if it's in use
        if (productsUsingDeletedSet.value.length) {
            showLoader.value = false;
            setInUseModal.value?.show();
            return;
        }

        await optionSetsApi.deleteSet(setToDelete);
        await refreshSets();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const cloneOptionSet = async (set: OptionSetDto) => {
    try {
        showLoader.value = true;
        await optionSetsApi.cloneOptionSet(set, forDesignTemplatesMode.value);
        await refreshSets();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const auditOptionSet = (set: OptionSetDto) => {
    selectedSet.value = set;
    showAuditLogModal.value = true;
};

const onAuditClose = () => {
    selectedSet.value = null;
    showAuditLogModal.value = false;
};

const addOptionSet = () => {
    try {
        router.push({ name: 'CreateOptionSet' });
    } catch (error) {
        window.captureException(error as Error);
    }
};

const editOptionSet = (setId: string | number) => {
    try {
        router.push({
            name: 'Set',
            query: {
                set: setId.toString()
            }
        });
    } catch (error) {
        window.captureException(error as Error);
    }
};

const handleClickRow = (set: unknown) => editOptionSet((set as OptionSetDto).id);

const openInNewTab = (event: MouseEvent, setId: string) => {
    event.stopPropagation();
    try {
        window.open(`${config.CUSTOMILY_BASE_URL}/store/set/?set=${setId}`);
    } catch (error) {
        window.captureException(error as Error);
    }
};

const copyId = (event: MouseEvent, id: string) => {
    event.stopPropagation();

    navigator.clipboard
        .writeText(id.toString())
        .then(() => {
            copiedId.value = id;
            setTimeout(() => {
                copiedId.value = '';
            }, 1000);
        })
        .catch((error) => {
            console.error('Error trying to copy id', error);
        });
};

const downloadOptionSetZips = (urls: string[]) => {
    const downloadNext = (index: number) => {
        if (index >= urls.length) return;

        const link = document.createElement('a');
        link.href = urls[index];
        link.target = '_parent';

        // The link has to be in the document for the click to work
        (document.body || document.documentElement).append(link);
        link.click();
        link.remove();

        // Download the next file with a small timeout
        setTimeout(() => downloadNext(index + 1), BETWEEN_DOWNLOADS_MS);
    };

    downloadNext(0);
};

const exportOptionSets = async () => {
    try {
        loaderMessage.value = EXPORTING_SETS_MESSAGE;
        showLoader.value = true;

        // One export per store, since the selection can span several of them
        const shopGroups = groupBy(selectedSets.value, (set) => set.shop);
        const responses = await Promise.all(
            Object.entries(shopGroups)
                .filter(([, sets]) => sets.length > 0)
                .map(([shopUrl, sets]) => optionSetsApi.exportOptionSets(shopUrl, sets.map((s) => s.id).join('_')))
        );

        downloadOptionSetZips(responses.map((response) => response.exportUrl));
    } catch (error) {
        window.captureException(error as Error);
    } finally {
        showLoader.value = false;
        loaderMessage.value = LOADING_SETS_MESSAGE;
    }
};

const showUploadFileModal = () => uploadFileModal.value?.show();

const closeUploadFileModal = () => uploadFileModal.value?.hide();

const importOptionSets = async () => {
    const targetStore = [...user.value.customilyUser.Stores]
        .sort((a, b) => a.Id - b.Id)
        .find((s) => s.Type === StoreType.Shopify || s.Type === StoreType.WooCommerce);

    if (!targetStore || !optionSetsZipFile.value) {
        console.error('Please select .zip file with options to import.');
        return;
    }

    try {
        uploadFileModal.value?.hide();
        showLoader.value = true;
        await optionSetsApi.importOptionSets(targetStore.Url, optionSetsZipFile.value);

        // The imported sets are the most recently updated ones, show them first
        sortBy.value = 'updated_at';
        sortMode.value = 'DESC';
        await applyQueryChange();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

const showTemplateSelectionModal = () => templatePickerModal.value?.show();

const onTemplateSelect = async (data: { template: CustomilyTemplateDto }) => {
    const result = await optionSetsApi.createSetFromTemplate(
        data.template.id.trim().toLowerCase(),
        user.value.customilyUser.IsManaged,
        `${data.template.name} (option set)`,
        '',
        shop.value
    );
    setTaskIdentifier.value = result.taskIdentifier;
};

const onTemplatePickerHidden = () => {
    setTaskIdentifier.value = '';
    router.go(0);
};

const openOptionSetLinkedToProductsModal = (item: OptionSetDto) => {
    selectedOptionSet.value = item;
    showOptionSetLinkedToProductsModal.value = true;
};

const closeOptionSetLinkedToProductsModal = () => {
    selectedOptionSet.value = undefined;
    showOptionSetLinkedToProductsModal.value = false;
};

const onOptionSetRename = async (id: string, newTitle: string) => {
    const set = optionSets.value.find((s) => s.id === id);
    if (!set) return;

    const title = String(newTitle).trim();
    if (!title || title === set.title) return;

    const previousTitle = set.title;
    set.title = title;

    try {
        await optionSetsApi.renameOptionSet(id, title);
    } catch (error) {
        set.title = previousTitle;
        window.captureException(error as Error);
    }
};

const onOptionSetVersionRestored = async (restoredVersion: EntityVersionRestored) => {
    const restoredOptionSet = restoredVersion.data as OptionSetDto;

    try {
        showLoader.value = true;
        const title = `${restoredOptionSet.title} (Restored)`;
        const setId = await optionSetsApi.addOptionSet(
            {
                title,
                options: restoredOptionSet.options
            },
            restoredOptionSet.forDesignTemplate
        );
        await optionSetsApi.saveOptionSet({
            id: setId,
            title,
            options: restoredOptionSet.options,
            sections: [],
            shop: restoredOptionSet.shop,
            linked: false,
            forDesignTemplate: restoredOptionSet.forDesignTemplate,
            clientId: restoredOptionSet.clientId,
            libraryId: restoredOptionSet.libraryId,
            createdFromTemplate: null
        });
        await refreshSets();
    } catch (error) {
        showLoader.value = false;
        window.captureException(error as Error);
    }
};

// Refresh requested by the legacy app embedding this view
useEventListener('message', (event) => {
    if ((event as MessageEvent).data?.action === 'refresh-sets-list') refreshSets();
});

onBeforeMount(async () => {
    if (user.value.customilyUser.AccessRoles.Serialized.includes('admin')) {
        commit('setDesignTemplatesMode', !!route.query['forDesignTemplates']);
    }

    // The list is only fetched once the real page size is known, so the first request already
    // asks for the rows that fit on screen
    pagination.pageSize = await whenMeasured();

    await refreshSets();
    pageLoaded.value = true;
});

return { __sfc: true,CURRENT_PAGE_STORAGE_KEY, FILTERS_STORAGE_KEY, FALLBACK_SETS_PER_PAGE, LOADING_SETS_MESSAGE, EXPORTING_SETS_MESSAGE, DELETE_CONFIRM_OPTIONS, BETWEEN_DOWNLOADS_MS, readStoredPage, readStoredFilter, state, commit, bvModal, router, route, user, shop, forDesignTemplatesMode, optionSets, selectedSets, selectedSet, selectedOptionSet, productsUsingDeletedSet, productsUsingDeletedSets, optionSetsZipFile, setTaskIdentifier, copiedId, showLoader, loaderMessage, pageLoaded, showAuditLogModal, showOptionSetLinkedToProductsModal, sortBy, sortMode, onlyLinkedOptionSets, entityType, pagination, setsList, setInUseModal, uploadFileModal, templatePickerModal, optionSetsLinkedToProductsModal, isManaged, isCustomStore, isAllChecked, optionSetsFiltersLabel, tableConfig, loadSets, getSetsCount, refreshSets, skipNextPageReload, goToFirstPage, applyQueryChange, whenMeasured, sortSets, sortModeSets, filterOptionSets, isSetIdSelected, toggleSetSelection, checkAll, handleOptionSetsBulkDelete, removeSet, cloneOptionSet, auditOptionSet, onAuditClose, addOptionSet, editOptionSet, handleClickRow, openInNewTab, copyId, downloadOptionSetZips, exportOptionSets, showUploadFileModal, closeUploadFileModal, importOptionSets, showTemplateSelectionModal, onTemplateSelect, onTemplatePickerHidden, openOptionSetLinkedToProductsModal, closeOptionSetLinkedToProductsModal, onOptionSetRename, onOptionSetVersionRestored, BModal, linkedOptionSets, Loader, CustomilyProductsList, AuditLog, SetProgression, CustomCheckbox, CButton, DynamicTable, CInput, EditableText, OptionSetLinkedToProductsModal }
}

})